import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./history.css";

const PAGE_SIZE = 15;

const STATUS = {
  approved: { label: "Approved" },
  rejected: { label: "Rejected" },
  pending:  { label: "Pending" },
};
const normalizeStatus = (s) => {
  const key = String(s ?? "").toLowerCase();
  return STATUS[key] ? key : "pending";
};
const FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

const fmtPts = (n) => Number(n || 0).toLocaleString("en-GB");
const fmtTnd = (pts) => (Number(pts || 0) / 2).toFixed(3); // 1 TND = 2 pts

const sameDay = (a, b) => a.toDateString() === b.toDateString();
const fmtDay = (t) => {
  if (!t) return "—";
  const d = new Date(t);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, now)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
const fmtTime = (t) =>
  t ? new Date(t).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "";
const monthKey = (t) => {
  if (!t) return "unknown";
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (t) =>
  t ? new Date(t).toLocaleDateString("en-GB", { month: "long", year: "numeric" }) : "Unknown date";

/* Icônes SVG intégrées (aucune librairie d'icônes nécessaire) */
const svgProps = {
  viewBox: "0 0 24 24", fill: "none", stroke: "currentColor",
  strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round",
  "aria-hidden": "true",
};
const StatusIcon = ({ status }) => {
  if (status === "approved")
    return <svg {...svgProps}><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>;
  if (status === "rejected")
    return <svg {...svgProps}><path d="M6 6l12 12M18 6L6 18" /></svg>;
  return <svg {...svgProps}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
};
const CircleCheck = () => (
  <svg {...svgProps}><circle cx="12" cy="12" r="9" /><path d="M8 12.5l3 3 5-6" /></svg>
);

const Skeleton = () => (
  <div className="wh-skeleton" aria-hidden="true">
    {[0, 1, 2, 3].map((i) => <div key={i} className="wh-skel-row" />)}
  </div>
);

const PopupHistory = ({ userId }) => {
  const API_URL = process.env.REACT_APP_API_URL;

  const [withdraws, setWithdraws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const fetchWithdrawHistory = useCallback(async (signal) => {
    if (!userId) {
      setError("We couldn't find your account. Please log in again.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/withdraws/${userId}`, {
        signal,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setWithdraws(
        data
          .map((w) => ({
            ...w,
            _time: w.createdAt ? new Date(w.createdAt).getTime() : 0,
            _status: normalizeStatus(w.status),
          }))
          .sort((a, b) => b._time - a._time)
      );
    } catch (err) {
      if (axios.isCancel(err)) return;
      console.error("Error fetching withdraw history:", err);
      setError("Your withdrawal history didn't load. Check your connection and try again.");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [API_URL, userId]);

  useEffect(() => {
    const c = new AbortController();
    fetchWithdrawHistory(c.signal);
    return () => c.abort();
  }, [fetchWithdrawHistory]);

  const changeFilter = (key) => {
    setFilter(key);
    setVisible(PAGE_SIZE);
  };

  const stats = useMemo(() => {
    const counts = { all: withdraws.length, pending: 0, approved: 0, rejected: 0 };
    let approvedPts = 0;
    withdraws.forEach((w) => {
      counts[w._status] += 1;
      if (w._status === "approved") approvedPts += Number(w.pointsWithdrawn) || 0;
    });
    return { counts, approvedPts };
  }, [withdraws]);

  const filtered = useMemo(
    () => (filter === "all" ? withdraws : withdraws.filter((w) => w._status === filter)),
    [withdraws, filter]
  );

  const groups = useMemo(() => {
    const map = new Map();
    filtered.slice(0, visible).forEach((w) => {
      const key = monthKey(w.createdAt);
      if (!map.has(key)) map.set(key, { key, label: monthLabel(w.createdAt), items: [] });
      map.get(key).items.push(w);
    });
    return Array.from(map.values());
  }, [filtered, visible]);

  const remaining = filtered.length - visible;

  if (loading)
    return (
      <div className="wh-root" role="status" aria-live="polite" aria-label="Loading history">
        <Skeleton />
      </div>
    );

  if (error)
    return (
      <div className="wh-root">
        <div className="wh-state wh-state--error" role="alert">
          <i className="wh-state__icon" aria-hidden="true">!</i>
          <p className="wh-state__text">History unavailable</p>
          <p className="wh-state__sub">{error}</p>
          <button type="button" className="wh-retry" onClick={() => fetchWithdrawHistory()}>Try again</button>
        </div>
      </div>
    );

  if (withdraws.length === 0)
    return (
      <div className="wh-root">
        <div className="wh-state">
          <i className="wh-state__icon" aria-hidden="true">↑</i>
          <p className="wh-state__text">No withdrawals yet</p>
          <p className="wh-state__sub">Requests you make will show up here with their status.</p>
        </div>
      </div>
    );

  return (
    <div className="wh-root">
      {/* En-tête */}
      <header className="wh-header">
        <h2 className="wh-title">Withdrawal history</h2>
        <p className="wh-subtitle">
          {stats.counts.all} request{stats.counts.all > 1 ? "s" : ""}
        </p>
      </header>

      {/* Grande carte dorée : total approuvé */}
      <div className="wh-hero">
        <div>
          <div className="wh-hero__label">Approved</div>
          <div className="wh-hero__value">{fmtPts(stats.approvedPts)} pts</div>
          <div className="wh-hero__hint">≈ {fmtTnd(stats.approvedPts)} TND</div>
        </div>
        <span className="wh-hero__icon"><CircleCheck /></span>
      </div>

      {/* Mini cartes */}
      <div className="wh-mini">
        <div className="wh-stat">
          <div className="wh-stat__label"><span className="wh-stat__dot" />Pending</div>
          <div className="wh-stat__value">{stats.counts.pending}</div>
        </div>
        <div className="wh-stat">
          <div className="wh-stat__label"><span className="wh-stat__dot wh-stat__dot--red" />Rejected</div>
          <div className="wh-stat__value">{stats.counts.rejected}</div>
        </div>
      </div>

      {/* Filtres en pastilles */}
      <div className="wh-seg" role="group" aria-label="Filter by status">
        {FILTERS.map((f) => (
          <button key={f.key} type="button" className="wh-chip"
            aria-pressed={filter === f.key} onClick={() => changeFilter(f.key)}>
            {f.label}
            <span className="wh-chip__count">{stats.counts[f.key]}</span>
          </button>
        ))}
      </div>

      {/* Liste */}
      <div aria-live="polite">
        {groups.length === 0 ? (
          <div className="wh-state" role="status">
            <i className="wh-state__icon" aria-hidden="true">∅</i>
            <p className="wh-state__text">No matching requests</p>
            <p className="wh-state__sub">
              You have no {FILTERS.find((f) => f.key === filter)?.label.toLowerCase()} withdrawals.
            </p>
            <button type="button" className="wh-link" onClick={() => changeFilter("all")}>Show all requests</button>
          </div>
        ) : (
          groups.map((g) => (
            <section className="wh-group" key={g.key} aria-label={g.label}>
              <h3 className="wh-month">
                <span>{g.label}</span>
                <span className="wh-month__count">{g.items.length}</span>
              </h3>
              <ul className="wh-rows">
                {g.items.map((w, i) => {
                  const pts = Number(w.pointsWithdrawn) || 0;
                  return (
                    <li key={w._id ?? `${w._time}-${i}`} className={`wh-row wh-row--${w._status}`}>
                      <span className="wh-row__icon"><StatusIcon status={w._status} /></span>
                      <span className="wh-row__info">
                        <span className="wh-row__day">{fmtDay(w.createdAt)}</span>
                        <span className="wh-row__sub">
                          {fmtTime(w.createdAt)} · <span className="wh-row__status">{STATUS[w._status].label}</span>
                        </span>
                      </span>
                      <span className="wh-row__amount">
                        <span className="wh-row__pts">{fmtPts(pts)}</span>
                        <span className="wh-row__unit">pts</span>
                        <span className="wh-row__tnd" style={{ display: "block" }}>≈ {fmtTnd(pts)} TND</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
      </div>

      {remaining > 0 && (
        <button type="button" className="wh-more" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
          Show {Math.min(remaining, PAGE_SIZE)} more ({remaining} remaining)
        </button>
      )}
    </div>
  );
};

export default PopupHistory;